<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { CockpitKpis } from '@/types/cockpit'
import KpiCard from './KpiCard.vue'

const props = defineProps<{
  kpis: CockpitKpis | null
  loading: boolean
  error: string | null
}>()

const emit = defineEmits<{
  retry: []
}>()

const { t, locale } = useI18n()

function formatMoney(value: string, currency: string): string {
  const num = parseFloat(value)
  return new Intl.NumberFormat(locale.value, { style: 'currency', currency: currency.toUpperCase() }).format(num)
}

function formatPercent(value: number | null): string {
  if (value === null) return '—'
  return (value * 100).toFixed(2) + '%'
}

const netPlatformFee = computed(() => {
  if (!props.kpis) return null
  return {
    value: formatMoney(props.kpis.kpis.net_platform_fee.value, props.kpis.currency),
    deltaPct: props.kpis.kpis.net_platform_fee.delta_pct,
  }
})

const grossVolume = computed(() => {
  if (!props.kpis) return null
  return {
    value: formatMoney(props.kpis.kpis.gross_volume.value, props.kpis.currency),
    deltaPct: props.kpis.kpis.gross_volume.delta_pct,
  }
})

const activeOrgs = computed(() => {
  if (!props.kpis) return null
  return {
    value: props.kpis.kpis.active_organisations.value.toLocaleString(locale.value),
    delta: props.kpis.kpis.active_organisations.delta ?? null,
    deltaPct: props.kpis.kpis.active_organisations.delta_pct ?? null,
  }
})

const paidRegistrations = computed(() => {
  if (!props.kpis) return null
  return {
    value: props.kpis.kpis.paid_registrations.value.toLocaleString(locale.value),
    deltaPct: props.kpis.kpis.paid_registrations.delta_pct ?? null,
  }
})

const stripeFees = computed(() => {
  if (!props.kpis) return null
  return {
    value: formatMoney(props.kpis.kpis.stripe_fees.value, props.kpis.currency),
    deltaPct: props.kpis.kpis.stripe_fees.delta_pct,
  }
})

const takeRate = computed(() => {
  if (!props.kpis) return null
  return {
    value: formatPercent(props.kpis.kpis.take_rate.value),
    deltaPct: props.kpis.kpis.take_rate.delta_pct,
  }
})
</script>

<template>
  <!-- Loading skeletons -->
  <div v-if="loading" class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
    <div
      v-for="i in 6"
      :key="i"
      class="bg-slate-800 border border-slate-700 rounded-2xl p-6 animate-pulse"
    >
      <div class="h-3 bg-slate-700 rounded w-2/3 mb-3" />
      <div class="h-8 bg-slate-700 rounded w-3/4 mb-2" />
      <div class="h-3 bg-slate-700 rounded w-1/3" />
    </div>
  </div>

  <!-- Error state -->
  <div
    v-else-if="error"
    class="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 flex items-center justify-between"
  >
    <p class="text-red-300 text-sm">{{ t(error) }}</p>
    <button
      type="button"
      class="text-sm text-red-300 hover:text-red-200 font-medium underline"
      @click="emit('retry')"
    >
      {{ t('admin.common.retry') }}
    </button>
  </div>

  <!-- KPI Cards -->
  <div v-else class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
    <KpiCard
      v-if="netPlatformFee"
      :label="t('admin.cockpit.kpi.net_platform_fee')"
      :value="netPlatformFee.value"
      :delta-pct="netPlatformFee.deltaPct"
      :subline="t('admin.cockpit.kpi.net_platform_fee_subline')"
      accent="emerald"
      :hero="true"
    />
    <KpiCard
      v-if="stripeFees"
      :label="t('admin.cockpit.kpi.stripe_fees')"
      :value="stripeFees.value"
      :delta-pct="stripeFees.deltaPct"
      :subline="t('admin.cockpit.kpi.stripe_fees_subline')"
      accent="muted"
    />
    <KpiCard
      v-if="grossVolume"
      :label="t('admin.cockpit.kpi.gross_volume')"
      :value="grossVolume.value"
      :delta-pct="grossVolume.deltaPct"
      :subline="t('admin.cockpit.kpi.gross_volume_subline')"
      accent="white"
    />
    <KpiCard
      v-if="activeOrgs"
      :label="t('admin.cockpit.kpi.active_organisations')"
      :value="activeOrgs.value"
      :delta="activeOrgs.delta"
      :subline="t('admin.cockpit.kpi.active_organisations_subline')"
      accent="white"
    />
    <KpiCard
      v-if="paidRegistrations"
      :label="t('admin.cockpit.kpi.paid_registrations')"
      :value="paidRegistrations.value"
      :delta-pct="paidRegistrations.deltaPct"
      accent="white"
    />
    <!--KpiCard
      v-if="takeRate"
      :label="t('admin.cockpit.kpi.take_rate')"
      :value="takeRate.value"
      :delta-pct="takeRate.deltaPct"
      :subline="t('admin.cockpit.kpi.take_rate_subline')"
      accent="white"
    /-->

    <!-- Empty state: kpis loaded but null data (shouldn't happen with valid API) -->
    <template v-if="!loading && !error && !kpis">
      <div class="col-span-6 text-center text-slate-500 py-8">
        {{ t('admin.cockpit.kpi.empty') }}
      </div>
    </template>
  </div>
</template>
